/**
 * MOVE ONE ORDER'S STATUS.
 *
 * The first actual write on this screen. Everything above it was a view.
 *
 * WHERE THE RULES LIVE: on the server, all of them. The lock on who may
 * advance, the status vocabulary, the reason requirement on a backward move,
 * the gate that stops an order entering production with a change request open,
 * and an echo check that refuses to report success on a write ClickUp accepted
 * but did not apply. This component sends an intent and renders the answer.
 *
 * It deliberately does NOT re-implement those rules to pre-empt them. A client
 * that decides for itself whether a move is allowed will eventually disagree
 * with the server, and the disagreement always favours the client — which is
 * how a guard quietly stops guarding. The one thing predicted locally is the
 * DIRECTION, and only to warn, never to block.
 *
 * A refusal here is usually the system working. It is shown as an explanation,
 * not as an error.
 */
import { useMemo, useState } from 'react'
import { api } from '../../lib/api.js'
import { useAccess } from '../../lib/access.jsx'
import { ACTIONS } from '../../lib/rbac.js'
import styles from './AdvanceOrder.module.css'

export default function AdvanceOrder({ order, deal, statuses, onDone }) {
  const access = useAccess()
  const can = access?.can || (() => false)
  const why = access?.why || (() => '')

  const allowed = can(ACTIONS.JOURNEY_PRODUCTION_UPDATE, deal)

  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null) // { tone, msg, detail }

  const current = String(order.status || '')

  /* Direction, predicted locally FOR THE WARNING ONLY. The server decides. */
  const direction = useMemo(() => {
    if (!target || !statuses?.length) return null
    const i = statuses.findIndex((s) => s.status.toLowerCase() === current.toLowerCase())
    const j = statuses.findIndex((s) => s.status.toLowerCase() === target.toLowerCase())
    if (i < 0 || j < 0) return 'unknown'
    if (i === j) return 'same'
    return j > i ? 'forward' : 'backward'
  }, [target, current, statuses])

  const submit = async () => {
    if (!target) return
    setBusy(true)
    setResult(null)
    try {
      const r = await api.journeyAdvance({
        taskId: order.id,
        status: target,
        reason: reason.trim() || undefined,
        from: current,
      })
      if (r?.noop) {
        setResult({ tone: 'hold', msg: r.reason || 'Already at that status. Nothing was written.' })
      } else {
        setResult({ tone: 'good', msg: `Moved to “${r.status || target}”.` })
        setReason('')
        setTarget('')
        onDone && onDone()
      }
    } catch (e) {
      const b = e?.body || {}
      /* The refusals worth naming, because each one means something different
       * and "request failed" would flatten all of them into noise. */
      const map = {
        journey_locked: 'You do not have standing to move orders on this board.',
        reason_required: 'That move needs a reason, and it goes on the permanent record.',
        unknown_status: 'That is not a status this list defines.',
        approval_outstanding: 'A customer has asked for changes on this order.',
        list_unreadable: 'The list’s statuses could not be read, so nothing was sent rather than sending it unverified.',
        echo_mismatch: 'ClickUp accepted the change but did not apply it, so this is not being reported as a success.',
      }
      setResult({
        tone: e?.status === 409 || e?.status === 400 ? 'hold' : 'bad',
        msg: map[b.error] || b.reason || e?.message || 'Could not move the order.',
        detail: b.reason && map[b.error] ? b.reason : null,
        outstanding: b.outstanding || null,
      })
      // A reason requirement should leave the box open and focused, not reset.
      if (b.error !== 'reason_required') setBusy(false)
    } finally {
      setBusy(false)
    }
  }

  if (!allowed) {
    return (
      <div className={styles.locked} title={why(ACTIONS.JOURNEY_PRODUCTION_UPDATE, deal)}>
        Read-only · {why(ACTIONS.JOURNEY_PRODUCTION_UPDATE, deal) || 'not permitted for your role'}
      </div>
    )
  }

  if (!open) {
    return (
      <div className={styles.row}>
        <button type="button" className={styles.btn} onClick={() => setOpen(true)}>
          Move status
        </button>
        {result && (
          <span className={`${styles.msg} ${styles['t_' + result.tone]}`}>{result.msg}</span>
        )}
      </div>
    )
  }

  return (
    <div className={styles.panel}>
      <div className={styles.currentLine}>
        Currently <strong>{current || 'unknown'}</strong>
      </div>

      <label className={styles.label}>
        Move to
        <select
          className={styles.select}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        >
          <option value="">Choose a status…</option>
          {(statuses || []).map((s) => (
            <option key={s.status} value={s.status} disabled={s.status.toLowerCase() === current.toLowerCase()}>
              {s.status}
            </option>
          ))}
        </select>
      </label>

      {direction === 'backward' && (
        <div className={styles.warn}>
          This moves the order <strong>backward</strong> on the board. That is a decision rather
          than progress, so the server will ask for a reason and keep it permanently.
        </div>
      )}

      <label className={styles.label}>
        Reason {direction === 'backward' ? <span className={styles.req}>required</span> : <span className={styles.opt}>optional</span>}
        <input
          className={styles.input}
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What changed, in a few words"
        />
      </label>

      {result && (
        <div className={`${styles.msg} ${styles['t_' + result.tone]}`}>
          {result.msg}
          {result.detail && <div className={styles.detail}>{result.detail}</div>}
          {result.outstanding?.length > 0 && (
            <ul className={styles.outstanding}>
              {result.outstanding.map((o, i) => (
                <li key={i}>{o.group}{o.at ? ` · ${String(o.at).slice(0, 10)}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={styles.row}>
        <button type="button" className={styles.btn} disabled={!target || busy} onClick={submit}>
          {busy ? 'Moving…' : 'Move it'}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.ghost}`}
          onClick={() => { setOpen(false); setResult(null); setTarget(''); setReason('') }}
        >
          Cancel
        </button>
      </div>

      <p className={styles.note}>
        Every move is written to a permanent ledger with your name on it, including refused
        ones. The change is verified against ClickUp before it is reported as done.
      </p>
    </div>
  )
}
